/**
 * Data layer for `invoice-attachment-bank-details-compliance-docs`. Thin typed fetch wrappers over /api/v1/invoice-attachment-bank-details-compliance-docs.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface InvoiceAttachmentBankDetailsComplianceDocsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListInvoiceAttachmentBankDetailsComplianceDocsResponse {
	count: number;
	items: InvoiceAttachmentBankDetailsComplianceDocsRow[];
}

export interface CreateInvoiceAttachmentBankDetailsComplianceDocsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/invoice-attachment-bank-details-compliance-docs";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListInvoiceAttachmentBankDetailsComplianceDocsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListInvoiceAttachmentBankDetailsComplianceDocsResponse>(res);
}

export async function create(body: CreateInvoiceAttachmentBankDetailsComplianceDocsBody): Promise<InvoiceAttachmentBankDetailsComplianceDocsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<InvoiceAttachmentBankDetailsComplianceDocsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const invoiceAttachmentBankDetailsComplianceDocsQueryKeys = {
	all: ["invoice-attachment-bank-details-compliance-docs"] as const,
	list: () => [...invoiceAttachmentBankDetailsComplianceDocsQueryKeys.all] as const,
	detail: (id: string) => [...invoiceAttachmentBankDetailsComplianceDocsQueryKeys.all, id] as const,
};
